import { Chip } from '@heroui/react';
import type { ReactNode } from 'react';
import {
  MarkAbsent,
  MarkCaution,
  MarkDatum,
  MarkInTolerance,
  MarkOutOfTolerance,
  MarkWaiting,
} from './marks.js';

/**
 * Status badges and inline alerts.
 *
 * Status TONE is derived from meaning, not chosen per page: an approved result
 * is green everywhere, a cancelled one is neutral everywhere. That consistency
 * is what lets someone scan a list without reading every word.
 *
 * The label always arrives translated. A status VALUE (`approved`,
 * `awaiting_review`) is an internal identifier and is never shown; the
 * catalogue turns it into a word.
 *
 * EVERY STATUS CARRIES A DRAWN MARK AS WELL AS A COLOUR. Colour alone fails
 * WCAG 2.2 AA, fails the roughly one man in twelve who cannot separate the
 * green from the red, and fails every screenshot that ends up printed in a
 * file. Three signals travel together — mark, word, colour — and any one of
 * them is enough to read the state.
 */

/**
 * `waiting` (D278, the approved redesign) is purple: a record waiting on
 * ANOTHER person — inspection, review, approval — kept apart from `warning`,
 * which asks the reader themselves to act.
 */
export type Tone = 'neutral' | 'info' | 'waiting' | 'success' | 'warning' | 'danger';

/**
 * `info` is the BLUE slot (`secondary` in `hero.ts`), as the palette defines it:
 * "in progress, informational, a reference value". It was mapped to `primary`,
 * which is AMBER — the same ramp as `warning` — so an informational notice and
 * a warning were drawn identically (UX pass: "the account is ready as soon as
 * you save" read as a caution).
 */
const CHIP_COLOR: Record<Tone, 'default' | 'secondary' | 'success' | 'warning' | 'danger'> = {
  neutral: 'default',
  info: 'secondary',
  waiting: 'default',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
};

const TONE_MARK: Record<Tone, (props: { size?: number }) => ReactNode> = {
  neutral: MarkAbsent,
  info: MarkDatum,
  waiting: MarkWaiting,
  success: MarkInTolerance,
  warning: MarkCaution,
  danger: MarkOutOfTolerance,
};

/**
 * The content padding beside the glyph, stated LOGICALLY.
 *
 * HeroUI's chip theme narrows the padding next to a start glyph with a PHYSICAL
 * `pl-*`, which is the correct side in English and the wrong side in Arabic.
 * Restating the size's own `px-*` lets the class merge drop that `pl-*`, and
 * `ps-*` puts the narrower side back next to the glyph in either direction —
 * identical to HeroUI's spacing in English, mirrored in Arabic.
 */
const CONTENT_PADDING: Record<NonNullable<StatusBadgeProps['size']>, string> = {
  sm: 'px-1 ps-0.5',
  md: 'px-2 ps-1',
};

/**
 * The badge's INK, per tone — stated here rather than left to HeroUI's flat
 * variant.
 *
 * HeroUI draws a flat chip's word in a mid shade (`text-warning-700`,
 * `text-default-700`, `text-primary-600`) on a 20–40% tint of the tone. Measured
 * in the browser, that is 3.1–4.3:1 — below WCAG 2.2 AA's 4.5:1 for text this
 * size. Each ink below is the lightest shade of its ramp that clears 4.5:1 on
 * its tone's tint over EVERY ground a badge sits on, from the white card down
 * to the sunken `content4` (#E8EBEF):
 *
 *   neutral  default-900  (800 measured 4.37:1 on content4 after D278)
 *   info     secondary-800 (blue) — measured in feedback-contrast.test.tsx
 *   success  success-700  4.7:1 at worst
 *   warning  warning-900  7.3:1 at worst   (800 is 4.5 on the page, 4.2 sunken)
 *   danger   danger-700   4.9:1 at worst
 *
 * Set on the chip's base, so the drawn mark (`currentColor`) and the word share
 * one ink; the label inherits it. The TONE a status carries is unchanged — this
 * is legibility, not meaning.
 */
const CHIP_INK: Record<Tone, string> = {
  neutral: 'text-default-900',
  info: 'text-secondary-800',
  /* Purple 700 on purple 50: 7.75:1. Its tint replaces the neutral one below. */
  waiting: 'text-waiting-700',
  success: 'text-success-700',
  warning: 'text-warning-900',
  danger: 'text-danger-700',
};

export interface StatusBadgeProps {
  /** Already translated. */
  readonly label: string;
  readonly tone?: Tone;
  readonly size?: 'sm' | 'md';
}

export function StatusBadge({
  label,
  tone = 'neutral',
  size = 'sm',
}: StatusBadgeProps) {
  const Glyph = TONE_MARK[tone];
  return (
    <Chip
      size={size}
      variant="flat"
      radius="sm"
      color={CHIP_COLOR[tone]}
      startContent={<Glyph size={12} />}
      classNames={{
        content: `font-medium ${CONTENT_PADDING[size]}`,
        base: `gap-1 px-2 ${CHIP_INK[tone]}${tone === 'waiting' ? ' bg-waiting-50' : ''}`,
      }}
    >
      {label}
    </Chip>
  );
}

/**
 * The tone a workflow status carries.
 *
 * Kept in one place so a status cannot look reassuring on one screen and
 * alarming on another. Anything unlisted is neutral, which is the honest
 * default: an unknown status should not be coloured as if its meaning were
 * understood.
 *
 * Note what is NOT here: `approved` is `success` because the WORKFLOW step
 * completed. It says nothing about whether the material passed — approval and
 * conformity are different things (CLAUDE.md section 8), and no colour in this
 * table is a scientific verdict.
 */
const STATUS_TONES: Record<string, Tone> = {
  // Progress
  new: 'info',
  awaiting_sample: 'waiting',
  awaiting_inspection: 'waiting',
  in_progress: 'info',
  not_started: 'neutral',
  partially_complete: 'warning',
  // Review and approval
  awaiting_review: 'waiting',
  awaiting_approval: 'waiting',
  returned_for_correction: 'warning',
  approved: 'success',
  // Terminal
  complete: 'success',
  accepted: 'success',
  accepted_with_notes: 'warning',
  held: 'warning',
  rejected: 'danger',
  cancelled: 'neutral',
  temporarily_stopped: 'warning',
  // Reports
  draft: 'neutral',
  issued_valid: 'success',
  suspended: 'warning',
  superseded: 'neutral',
  // Readiness
  ready: 'success',
  not_ready: 'warning',
};

export function toneForStatus(status: string): Tone {
  return STATUS_TONES[status] ?? 'neutral';
}

// ---------------------------------------------------------------------------
// Alerts
// ---------------------------------------------------------------------------

const ALERT_CLASSES: Record<Tone, string> = {
  neutral: 'border-default-200 bg-default-50 text-default-700',
  info: 'border-secondary-200 bg-secondary-50 text-secondary-800',
  waiting: 'border-waiting-100 bg-waiting-50 text-waiting-700',
  success: 'border-success-200 bg-success-50 text-success-800',
  warning: 'border-warning-200 bg-warning-50 text-warning-800',
  danger: 'border-danger-200 bg-danger-50 text-danger-800',
};

export interface AlertProps {
  /** Already translated. */
  readonly title: string;
  readonly description?: string;
  readonly tone?: Tone;
  readonly action?: ReactNode;
}

/**
 * A standing message about the state of the page.
 *
 * Distinct from a toast: an alert is part of the page and stays as long as the
 * condition does. A toast reports a moment. Warnings and failures take
 * `role="alert"` so assistive technology announces them; softer tones are
 * ordinary content.
 */
export function Alert({
  title,
  description,
  tone = 'info',
  action,
}: AlertProps) {
  const assertive = tone === 'danger' || tone === 'warning';
  const Glyph = TONE_MARK[tone];
  return (
    <div
      {...(assertive ? { role: 'alert' as const } : {})}
      className={`flex flex-wrap items-start gap-stack rounded-medium border px-4 py-3 text-body ${ALERT_CLASSES[tone]}`}
    >
      {/* The mark repeats the tone for a reader who cannot use the colour. */}
      <span className="mt-0.5 shrink-0">
        <Glyph size={16} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-medium">{title}</p>
        {/* Full ink, like the title. An opacity fade took the body below
            WCAG AA (4.2:1 on the warning ground, measured). */}
        {description ? <p className="mt-1">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}
